import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { GraduationCap, Search, Calendar, Users, ChevronDown, ChevronRight } from 'lucide-react';
import LoadingSpinner from '../../common/LoadingSpinner';

export default function CourseSessionSelector({ selectedSessions, onSelectionChange }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const [courses, setCourses] = useState([]);
    const [sessions, setSessions] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [expandedCourses, setExpandedCourses] = useState([]);

    useEffect(() => {
        fetchCourses();
    }, []);

    const fetchCourses = async () => {
        try {
            setLoading(true);
            const api = getAuthenticatedApi();
            
            const coursesResponse = await api.get('/gym_courses.php?action=list_courses');
            const coursesData = coursesResponse.data.courses || [];
            setCourses(coursesData.filter(c => c.status === 'active'));

            const sessionsData = {};
            
            // Carica sessioni per i prossimi 6 mesi per iscrizione corsi
            const today = new Date();
            const months = [];
            for (let i = 0; i < 6; i++) {
                const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
                months.push(date.toISOString().slice(0, 7));
            }
            
            // Carica sessioni per tutti i mesi
            const allSessionsPromises = months.map(month => 
                api.get(`/gym_courses.php?action=list_sessions&month=${month}`)
            );
            
            const sessionsResponses = await Promise.all(allSessionsPromises);
            const allSessions = sessionsResponses.flatMap(response => response.data.sessions || []);
            
            // Filtra solo sessioni future e raggruppa per corso
            const todayStr = today.toISOString().split('T')[0];
            allSessions.forEach(session => {
                if (!sessionsData[session.course_id]) {
                    sessionsData[session.course_id] = [];
                }
                if (session.session_date >= todayStr) {
                    sessionsData[session.course_id].push(session);
                }
            });
            
            // Ordina le sessioni per data
            Object.keys(sessionsData).forEach(courseId => {
                sessionsData[courseId].sort((a, b) => 
                    new Date(a.session_date) - new Date(b.session_date)
                );
            });

            setSessions(sessionsData);
        } catch (err) {
            console.error('Errore caricamento corsi:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredCourses = courses.filter(course => {
        const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (course.description || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    const categories = ['all', ...new Set(courses.map(c => c.category).filter(Boolean))];

    const toggleCourse = (courseId) => {
        setExpandedCourses(prev =>
            prev.includes(courseId)
                ? prev.filter(id => id !== courseId)
                : [...prev, courseId]
        );
    };

    const handleSessionToggle = (sessionId) => {
        const newSelection = selectedSessions.includes(sessionId)
            ? selectedSessions.filter(id => id !== sessionId)
            : [...selectedSessions, sessionId];
        onSelectionChange(newSelection);
    };

    const handleSelectAllCourseSessions = (courseId) => {
        const courseSessions = sessions[courseId] || [];
        const sessionIds = courseSessions.map(s => s.id);
        
        const allSelected = sessionIds.every(id => selectedSessions.includes(id));
        
        if (allSelected) {
            onSelectionChange(selectedSessions.filter(id => !sessionIds.includes(id)));
        } else {
            onSelectionChange([...new Set([...selectedSessions, ...sessionIds])]);
        }
    };

    const getSelectedCount = (courseId) => {
        const courseSessions = sessions[courseId] || [];
        return courseSessions.filter(s => selectedSessions.includes(s.id)).length;
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-4 h-full flex flex-col">
            <div className="flex items-center gap-2 mb-4">
                <GraduationCap className="text-indigo-600" size={24} />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    2️⃣ {t('gym.bulkEnrollment.courses.title')}
                </h3>
            </div>

            <div className="space-y-3 mb-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder={t('gym.bulkEnrollment.courses.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                </div>

                <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500"
                >
                    {categories.map(cat => (
                        <option key={cat} value={cat}>
                            {cat === 'all' ? t('gym.bulkEnrollment.courses.allCategories') : cat}
                        </option>
                    ))}
                </select>

                <div className="text-sm text-gray-600 dark:text-white/65">
                    {selectedSessions.length} {t('gym.bulkEnrollment.courses.sessionsSelected')}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2">
                {filteredCourses.length > 0 ? (
                    filteredCourses.map(course => {
                        const courseSessions = sessions[course.id] || [];
                        const isExpanded = expandedCourses.includes(course.id);
                        const selectedCount = getSelectedCount(course.id);
                        const allSelected = courseSessions.length > 0 && 
                                          courseSessions.every(s => selectedSessions.includes(s.id));

                        return (
                            <div key={course.id} className="border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
                                <div className="bg-gray-50 dark:bg-gray-800 p-3">
                                    <div className="flex items-center justify-between">
                                        <button
                                            onClick={() => toggleCourse(course.id)}
                                            className="flex items-center gap-2 flex-1 text-left"
                                        >
                                            {isExpanded ? (
                                                <ChevronDown size={20} className="text-gray-500" />
                                            ) : (
                                                <ChevronRight size={20} className="text-gray-500" />
                                            )}
                                            {course.logo_filename ? (
                                                <img
                                                    src={`/api/serve_image.php?filename=${course.logo_filename}&type=logo`}
                                                    alt={course.title}
                                                    className="w-8 h-8 object-cover rounded-lg border border-gray-200 dark:border-white/10 flex-shrink-0"
                                                />
                                            ) : (
                                                <div
                                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: course.color || '#3B82F6' }}
                                                />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-gray-900 dark:text-white text-sm">
                                                    {course.title}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-white/65">
                                                    {courseSessions.length} {t('gym.bulkEnrollment.courses.futureSessions')}
                                                    {selectedCount > 0 && ` • ${selectedCount} ${t('gym.bulkEnrollment.courses.sessionsSelected')}`}
                                                </p>
                                            </div>
                                        </button>
                                        
                                        {courseSessions.length > 0 && (
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={allSelected}
                                                    onChange={() => handleSelectAllCourseSessions(course.id)}
                                                    className="w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                                                />
                                                <span className="text-sm text-gray-600 dark:text-white/65">
                                                    {t('gym.bulkEnrollment.courses.selectAllSessions')}
                                                </span>
                                            </label>
                                        )}
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="p-2 space-y-1">
                                        {courseSessions.length > 0 ? (
                                            courseSessions.map(session => (
                                                <label
                                                    key={session.id}
                                                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                                                        selectedSessions.includes(session.id)
                                                            ? 'bg-indigo-50 dark:bg-indigo-900/20'
                                                            : 'hover:bg-gray-50 dark:hover:bg-white/[0.06]'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedSessions.includes(session.id)}
                                                        onChange={() => handleSessionToggle(session.id)}
                                                        className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 text-sm">
                                                            <Calendar size={14} className="text-gray-400" />
                                                            <span className="font-medium text-gray-900 dark:text-white">
                                                                {new Date(session.session_date).toLocaleDateString('it-IT', { 
                                                                    weekday: 'short', 
                                                                    day: 'numeric', 
                                                                    month: 'short' 
                                                                })}
                                                            </span>
                                                            <span className="text-gray-600 dark:text-white/65">
                                                                {session.start_time?.slice(0, 5)} - {session.end_time?.slice(0, 5)}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-white/65 mt-1">
                                                            <Users size={12} />
                                                            <span>
                                                                {session.current_participants || 0}/{session.max_participants || '∞'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </label>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-500 dark:text-white/65 text-center py-2">
                                                {t('gym.bulkEnrollment.courses.noFutureSessions')}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center py-8">
                        <GraduationCap size={48} className="mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-500 dark:text-white/65 text-sm">
                            {t('gym.bulkEnrollment.courses.noCoursesFound')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
